import { Fragment, useEffect, useState } from 'react';
import { authApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { PleinEcran } from '../../components/ui/PleinEcran.jsx';
import { tr, trf } from '../../lib/i18n';
import { numeroLisible } from '../../lib/telephone';
import { C } from '../../theme/palette';
import { ChoixSecuriteEcran } from '../auth/ChoixSecuriteEcran.jsx';
import { RelierApplication } from '../plus/RelierApplication.jsx';
import { FeuilleCodesSecours, ListeCodes } from './CodesSecours.jsx';

/** Ce que montre la ligne, selon la 2ᵉ sécurité qui sert à la connexion. */
const LIBELLES = {
  EMAIL: '✉️ Code par e-mail',
  SMS: '📱 Code par SMS',
  APPLICATION: '🔑 Application Authenticator',
};

/** Le jour d'un horodatage du serveur, à la suisse : « 31.10.2026 ». */
function jourSuisse(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const deux = (n) => String(n).padStart(2, '0');
  return deux(d.getDate()) + '.' + deux(d.getMonth() + 1) + '.' + d.getFullYear();
}

/** « Il faut **au moins un** moyen » : les passages entre ** s'écrivent en gras. */
function gras(texte) {
  return String(texte)
    .split('**')
    .map((morceau, i) =>
      i % 2 ? <b key={i}>{morceau}</b> : <Fragment key={i}>{morceau}</Fragment>,
    );
}

/**
 * L'interrupteur d'un moyen, comme sur la maquette.
 *
 * @param {object} props propriétés
 * @param {boolean} props.allume vrai si le moyen est allumé
 * @param {string} props.libelle nom lu par le lecteur d'écran
 * @param {boolean} [props.inactif] grisé, sans effet
 * @param {Function} props.onClick bascule
 * @returns {JSX.Element} l'interrupteur
 */
function Interrupteur({ allume, libelle, inactif, onClick }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={allume}
      aria-label={libelle}
      disabled={inactif}
      onClick={inactif ? undefined : onClick}
      style={{
        width: 46,
        height: 28,
        borderRadius: 14,
        border: 'none',
        padding: 0,
        flex: 'none',
        position: 'relative',
        background: allume ? C.teal : '#CBD5E1',
        cursor: inactif ? 'not-allowed' : 'pointer',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 3,
          left: allume ? 21 : 3,
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left .15s',
        }}
      />
    </button>
  );
}

/** Une ligne de moyen : son nom, son détail, son interrupteur. */
function Ligne({ titre, detail, allume, inactif, onClick }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 10,
        border: '1.5px solid ' + C.border,
        borderRadius: 12,
        padding: 12,
        marginBottom: 10,
        fontSize: 14,
        color: C.ink,
        opacity: inactif ? 0.45 : 1,
      }}
    >
      <span style={{ minWidth: 0 }}>
        {titre}
        <small style={{ display: 'block', color: C.grey, fontSize: 12, marginTop: 3 }}>
          {detail}
        </small>
      </span>
      <Interrupteur allume={allume} libelle={titre} inactif={inactif} onClick={onClick} />
    </div>
  );
}

/** Ce que chaque action demande de confirmer, avec le mot de passe. */
const QUESTIONS = {
  EMAIL_OFF: 'Éteindre le code par e-mail ?',
  EMAIL_ON: 'Rallumer le code par e-mail ?',
  SMS_OFF: 'Éteindre le code par SMS ?',
  SMS_ON: 'Allumer le code par SMS ?',
  APPLICATION_OFF: 'Éteindre l’application Authenticator ? Elle sera retirée de ton compte.',
  SECOURS: 'Créer de nouveaux codes de secours ? Les anciens ne marcheront plus.',
};

/**
 * « Ta 2ᵉ sécurité » dans Réglages → Mon compte : d'où peut venir le code
 * demandé après le mot de passe — l'application Authenticator, l'e-mail, le
 * SMS —, chacun avec son interrupteur. Le patron peut couper l'e-mail et ne
 * garder que l'application.
 *
 * <p>Il reste toujours au moins un moyen allumé : l'écran le dit, et le
 * serveur refuse d'éteindre le dernier. Chaque changement se confirme par le
 * mot de passe, comme ailleurs dans Sécurité. Les codes de secours ne se
 * montrent qu'une fois, quand ils sont créés — et quand on éteint l'e-mail
 * sans en avoir — : « 🖨 Imprimer mes codes de secours » les met sur une
 * feuille A4.
 *
 * <p>« Changer ma 2ᵉ sécurité » choisit toujours celle qui sert d'abord.
 *
 * @returns {JSX.Element|null} la carte, ou rien pour un compte sans adresse
 */
export function MaSecurite() {
  const { session } = useSession();
  // L'ouvrier entré par son code PIN n'a pas d'adresse où recevoir un code.
  const sansAdresse = !session || session.accesParPin;
  const [etat, setEtat] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  // Action qui attend le mot de passe : EMAIL_OFF, SMS_ON, SECOURS…
  const [action, setAction] = useState(null);
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  // Les codes de secours en clair, montrés cette seule fois.
  const [codes, setCodes] = useState(null);
  const [feuille, setFeuille] = useState(false);
  const [relier, setRelier] = useState(false);

  // Dans une promesse : une erreur de l'appel lui-même laisse la carte vide,
  // sans emporter l'écran qui la contient (l'espace de l'ouvrier).
  const relit = () =>
    Promise.resolve()
      .then(() => authApi.etatDoubleAuth())
      .then(setEtat)
      .catch(() => setEtat(null));

  useEffect(() => {
    if (!sansAdresse) relit();
  }, [sansAdresse]);

  if (sansAdresse || !etat) return null;

  const changer = (
    <>
      <Btn
        label={tr('Changer ma 2ᵉ sécurité')}
        ghost
        color={C.navy}
        full
        onClick={() => setOuvert(true)}
      />
      {ouvert && (
        <PleinEcran>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9000,
              overflowY: 'auto',
              background: C.bg,
            }}
          >
            <ChoixSecuriteEcran
              depuisReglages
              sansReport={false}
              onTermine={() => {
                setOuvert(false);
                relit();
              }}
            />
          </div>
        </PleinEcran>
      )}
    </>
  );

  // Aucun code n'est demandé à ce compte : rien à allumer ni à éteindre.
  if (!etat.active) {
    const libelle = LIBELLES[etat.methode] || 'Aucune';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: C.navy }}>
          🔒 {tr('Ta 2ᵉ sécurité')} : {tr(libelle)}
        </div>
        {changer}
      </div>
    );
  }

  const application = Boolean(etat.application);
  const email = etat.email !== false;
  const sms = Boolean(etat.sms);
  const allumes = [application, email, sms].filter(Boolean).length;
  const smsPossible = Boolean(etat.smsDisponible && etat.telephone);

  /** Demande le mot de passe pour une action, sauf si elle éteindrait le dernier moyen. */
  const demande = (quoi, eteint) => {
    setErreur('');
    if (eteint && allumes <= 1) {
      setErreur(tr('Il faut toujours au moins un moyen allumé. Allume d’abord un autre moyen.'));
      return;
    }
    setMotDePasse('');
    setAction(quoi);
  };

  const confirme = async () => {
    setErreur('');
    setEnvoi(true);
    try {
      if (action === 'SECOURS') {
        const reponse = await authApi.refaitCodesSecours(motDePasse);
        setCodes(reponse?.codes || []);
      } else if (action === 'APPLICATION_OFF') {
        setEtat(await authApi.retireApplication(motDePasse));
      } else {
        const [moyen, sens] = action.split('_');
        const reponse = await authApi.regleMoyen(moyen, sens === 'ON', motDePasse);
        if (reponse?.etat) setEtat(reponse.etat);
        if (reponse?.codesSecours?.length) setCodes(reponse.codesSecours);
      }
      setAction(null);
      setMotDePasse('');
      if (action === 'SECOURS' || action === 'APPLICATION_OFF') relit();
    } catch (e) {
      setErreur(e?.message || tr('La demande n’a pas abouti.'));
    } finally {
      setEnvoi(false);
    }
  };

  const detailSms = !etat.smsDisponible
    ? tr('Dès que le service SMS sera branché')
    : !etat.telephone
      ? tr('Ajoute ton numéro de portable dans Plus → Sécurité')
      : numeroLisible(etat.telephone);

  const restants = Number(etat.codesSecours) || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div
        style={{
          background: C.white,
          borderRadius: 16,
          padding: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,.06)',
          color: C.ink,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 10 }}>
          🔐 {tr('Ta 2ᵉ sécurité — d’où peut venir le code')}
        </div>
        <Ligne
          titre={tr('🔑 Application Authenticator')}
          detail={
            application
              ? etat.applicationDepuis
                ? trf('Installée le {0}', jourSuisse(etat.applicationDepuis))
                : tr('Installée')
              : tr('Pas encore installée')
          }
          allume={application}
          onClick={() => (application ? demande('APPLICATION_OFF', true) : setRelier(true))}
        />
        <Ligne
          titre={tr('✉️ Code par e-mail')}
          detail={etat.emailMasque || session.email}
          allume={email}
          onClick={() => demande(email ? 'EMAIL_OFF' : 'EMAIL_ON', email)}
        />
        <Ligne
          titre={tr('📱 Code par SMS')}
          detail={detailSms}
          allume={sms}
          inactif={!smsPossible}
          onClick={() => demande(sms ? 'SMS_OFF' : 'SMS_ON', sms)}
        />

        {action && (
          <div
            role="group"
            aria-label={tr('Mot de passe actuel, pour confirmer')}
            style={{
              background: C.navyXL,
              borderRadius: 10,
              padding: '10px 12px',
              marginBottom: 10,
            }}
          >
            <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8 }}>
              {tr(QUESTIONS[action])}
            </div>
            <Inp
              label={tr('Mot de passe actuel, pour confirmer')}
              type="password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn
                label={envoi ? tr('Un instant…') : tr('Confirmer')}
                color={C.teal}
                full
                disabled={envoi || !motDePasse}
                onClick={confirme}
              />
              <Btn
                label={tr('Annuler')}
                ghost
                color={C.grey}
                onClick={() => {
                  setAction(null);
                  setMotDePasse('');
                  setErreur('');
                }}
              />
            </div>
          </div>
        )}

        {erreur && (
          <div
            role="alert"
            style={{ fontSize: 11.5, color: C.red, fontWeight: 700, marginBottom: 10 }}
          >
            {erreur}
          </div>
        )}

        <div
          style={{
            background: '#FEF3C7',
            color: '#92400E',
            borderRadius: 10,
            padding: '10px 12px',
            fontSize: 12.5,
            lineHeight: 1.5,
          }}
        >
          ⚠️{' '}
          {gras(
            tr(
              'Il faut toujours **au moins un** moyen allumé. Si tu éteins l’e-mail et que tu perds ton téléphone, tu entres avec un **code de secours** :',
            ),
          )}
          {codes?.length ? (
            <>
              <ListeCodes codes={codes} />
              <div style={{ marginTop: 6 }}>{tr('Chaque code ne sert qu’une fois.')}</div>
              <div style={{ marginTop: 4, fontWeight: 700 }}>
                {tr('Note-les ou imprime-les maintenant : ils ne seront plus montrés.')}
              </div>
              <button
                type="button"
                onClick={() => setFeuille(true)}
                style={{
                  display: 'inline-block',
                  marginTop: 10,
                  border: '2px solid ' + C.navy,
                  borderRadius: 10,
                  padding: '8px 12px',
                  fontWeight: 800,
                  fontSize: 13,
                  background: 'transparent',
                  color: '#92400E',
                  cursor: 'pointer',
                }}
              >
                {tr('🖨 Imprimer mes codes de secours')}
              </button>
            </>
          ) : (
            <div style={{ marginTop: 6 }}>
              {restants > 0
                ? trf('Il te reste {0} codes de secours sur 6.', restants)
                : tr('Tu n’as pas encore de codes de secours.')}
            </div>
          )}
          <button
            type="button"
            onClick={() => demande('SECOURS', false)}
            style={{
              display: 'block',
              marginTop: 10,
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#92400E',
              fontWeight: 700,
              fontSize: 12,
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {restants > 0 || codes?.length
              ? tr('Refaire de nouveaux codes de secours')
              : tr('Créer mes codes de secours')}
          </button>
        </div>
      </div>
      {changer}
      {feuille && codes?.length > 0 && (
        <FeuilleCodesSecours codes={codes} onFerme={() => setFeuille(false)} />
      )}
      {relier && (
        <PleinEcran>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9000,
              overflowY: 'auto',
              background: C.bg,
              padding: 'calc(var(--st-haut, 0px) + 14px) 14px 14px',
            }}
          >
            <div
              style={{
                maxWidth: 480,
                margin: '0 auto',
                background: C.white,
                borderRadius: 14,
                padding: 14,
              }}
            >
              <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10 }}>
                {tr('🔑 Application Authenticator')}
              </div>
              <RelierApplication
                onRelie={() => {
                  setRelier(false);
                  relit();
                }}
              />
              <button
                type="button"
                onClick={() => setRelier(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: C.teal,
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                  marginTop: 12,
                  display: 'block',
                  width: '100%',
                }}
              >
                {tr('← Retour sans rien changer')}
              </button>
            </div>
          </div>
        </PleinEcran>
      )}
    </div>
  );
}
